<template>
  <div>
    <div v-if="onlyButton">
      <vs-button color="secondary" type="border" @click="handleClickInput">{{ btnTitle }}</vs-button>
    </div>
    <div v-else-if="multiple" style="display: grid; grid-template-columns: 1fr auto; border: 1px solid #cdcdcd; border-radius: 5px; padding: 0.35rem 0">
      <div style="padding: 0 0.35rem; display: flex; gap: 0.375rem; flex-wrap: wrap; overflow-y: auto; max-height: 8rem">
        <vs-chip v-for="field in initFields" :key="field.id" class="chip square m-0" closable @click="removeFieldItem(field)">
          {{ field.field_name }}
        </vs-chip>
        <vs-input class="hidden" style="border-radius: 5px" :value="selectField.field_name" :readonly="readonly" />
      </div>
      <div class="grid items-center cursor-pointer" style="padding-left: 6px; padding-right: 6px; border-left: 1px solid #cdcdcd" @click="handleClickInput">
        <vs-icon style="font-size: 24px" icon-pack="feather" icon="icon-home"></vs-icon>
      </div>
    </div>

    <!-- 입력칸과 🏠 아이콘 어디를 눌러도 선택 팝업이 열리도록 감싸는 label 에서 클릭을 받는다 -->
    <label v-else style="display: flex; position: relative; align-items: center" :class="readonly ? '' : 'cursor-pointer'" @click="handleClickInput">
      <vs-input class="w-full h-full" style="border-radius: 5px" :value="selectField.field_name" :readonly="readonly" />
      <vs-icon icon-pack="feather" icon="icon-home" style="padding-left: 6px; padding-right: 6px; position: absolute; top: auto; right: 0; bottom: auto; font-size: 24px; border-left: 1px solid #cdcdcd"></vs-icon>
    </label>

    <popup-search-field v-if="!fieldTypes" v-model="showPopup" :use-types="useTypes" :selectId="selectField.id" :show-all="showAll" @handleClickSelect="handleClickSelect" />
    <popup-search-field-by-type
      v-else
      v-model="showPopup"
      :field-types="fieldTypes"
      :project-info="projectInfo"
      :menu-title="menuTitle"
      :selectId="selectField.id"
      @handleClickSelect="handleClickSelect"
      :multiple="multiple"
      :init-fields="initFields"
      @handleClickMultipleSelect="handleClickMultipleSelect"
      :select-all-active="selectAllActive"
      :show-all="showAll"
    />
  </div>
</template>

<script>
import PopupSearchField from "@/popup/PopupSearchField";
import PopupSearchFieldByType from "@/popup/PopupSearchFieldByType.vue";

export default {
  name: "FieldSelector",
  components: { PopupSearchFieldByType, PopupSearchField },
  props: {
    value: [Number, String, Array, Object],
    fieldName: String,
    readonly: Boolean,
    btnTitle: {
      type: String,
      default: "부서 선택",
    },
    popupTitle: {
      type: String,
      default: "",
    },
    useTabCodes: Array,
    multiple: Boolean,
    onlyButton: Boolean,
    selectAllActive: Boolean,
    // 참여 여부와 상관없이 모든 사업장을 조회하도록하는 경우
    showAll: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      async handler() {
        if (this.multiple) {
          this.initFields = this.value;
        } else if (this.value) {
          // this.value 값이 field_id 값이 있을 경우도 있고, this.value.id 값이 있을 경우도 있다
          const fieldId = this.value.id ? this.value.id : this.value;

          this.initField = await this.$store.dispatch("project/LOAD_FIELD", { postId: fieldId });
        }
      },
    },
    fieldListInfo: {
      deep: true,
      handler(/*value*/) {},
    },
    menuTitle: {
      deep: true,
      handler(/*value*/) {},
    },
  },
  computed: {
    projectInfo() {
      return this.$store.state.project.projectInfo;
    },
    fieldListInfo() {
      return this.$store.state.project.fieldListInfo;
    },
    fieldTypes() {
      let list = [];
      if (this.useTabCodes && this.useTabCodes.length > 0) {
        if (this.projectInfo.field_types && this.projectInfo.field_types.length > 0) {
          for (const type of this.projectInfo.field_types) {
            if (this.useTabCodes.indexOf(type.code) >= 0) {
              list.push(type);
            }
          }
        }
      } else if (this.projectInfo.field_types) {
        list = this.projectInfo.field_types;
      }

      return list;
    },
    type() {
      // 2023-03-06 추가된 방식.. FieldType 모델과 M:N 관계에 따라서 탭 목록이 변경된다
      if (typeof this.projectInfo.field_types !== "undefined") {
        const fieldTypes = this.projectInfo.field_types;
        if (fieldTypes && fieldTypes.length > 0) {
          return fieldTypes.map((item) => item.name);
        }
      }

      // 기존 방식.. project_type에 따라서 표시되는 탭 목록이 변경된다
      if (typeof this.projectInfo.project_type !== "undefined") {
        if (this.projectInfo.project_type.indexOf("총괄관리") >= 0) {
          //ProjectId = 1
          return ["사업장(직접수행)", "사업장(용역위탁)", "사업장(도급발주)", "시설물"];
        } else if (this.projectInfo.project_type.indexOf("내부종사자") >= 0) {
          //ProjectId = 2
          return ["사업장(직접수행)"];
        } else if (this.projectInfo.project_type.indexOf("도급") >= 0) {
          //ProjectId = 3
          return ["사업장(용역위탁)"];
        } else if (this.projectInfo.project_type.indexOf("발주") >= 0) {
          //ProjectId = 4
          return ["사업장(도급발주)"];
        } else if (this.projectInfo.project_type.indexOf("중대시민재해") >= 0) {
          //ProjectId = 5
          return ["시설물"];
        }
      }
    },
    useTypes() {
      if (typeof this.type === "string") {
        return [this.type];
      } else {
        return this.type;
      }
    },
    selectField() {
      // 만약 초기에 설정된 값이 있으면 표시한다
      if (this.initField.id) {
        return {
          field_name: this.initField.field_name,
          id: this.initField.id,
        };
      }

      const info = this.fieldListInfo;

      if (info.data) {
        const list = this.$store.state.project.fieldListInfo.data;
        for (const field of list) {
          if (Number(`${field.id}`) === Number(`${this.value}`)) {
            return field;
          }
        }
      }
      return {
        field_name: this.fieldName ? this.fieldName : "",
        id: 0,
      };
    },
    menuTitle() {
      return this.popupTitle;
    },
  },
  data() {
    return {
      showPopup: false,
      initField: {},
      initFields: [],
    };
  },
  async created() {
    if (this.multiple) {
      this.initFields = this.value;
    } else if (this.value) {
      const fieldId = this.value.id ? this.value.id : this.value;
      this.initField = await this.$store.dispatch("project/LOAD_FIELD", { postId: fieldId });
    }
  },
  methods: {
    handleClickInput() {
      if (!this.readonly) {
        this.showPopup = true;
      }
    },

    handleClickSelect(field) {
      // 초기에 전달됐던 데이터는 초기화한다
      this.initField = {};

      this.$emit("input", field.id);
      this.$emit("select", field);
    },

    removeFieldItem(field) {
      this.initFields = this.initFields.filter((item) => {
        return `${item.id}` !== `${field.id}`;
      });

      this.$emit("input", this.initFields);
      this.$emit("select", this.initFields);
    },
    handleClickMultipleSelect(fields) {
      this.initFields = fields;

      this.$emit("input", fields);
      this.$emit("select", fields);
    },
  },
};
</script>

<style lang="scss">
.chip.square {
  overflow: hidden;
  flex-shrink: 0;

  background: #ffffff;
  border: solid 1px #d7d7d7;

  border-radius: 5px;

  & > span {
    flex: 1 0 auto;

    font-size: 13px;
    color: #2c2c2c;

    text-overflow: ellipsis;
  }

  & > button {
    color: #2c2c2c;
    background: inherit;
  }
}
</style>
